import React, { Fragment, useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import Header from '../../../components/Header';
import Appfooter from '../../../components/Appfooter';
import Load from '../../../components/Load';
import {
  HORARIOS_PERMITIDOS, CRIAR_HORARIO_PERMITIDO, DELETAR_HORARIO_PERMITIDO, DELETAR_TODOS_HORARIOS,
  CRIAR_PRECO_QUADRA, DELETAR_PRECO_QUADRA, DELETAR_TODOS_PRECOS,
  DIAS_SEMANA, gerarHorarios, QUADRA_ADMIN, QUADRAS_POR_LOCAL, ATUALIZAR_QUADRA,
} from '../../../services/quadraAdminService';
import { PRECOS_QUADRA } from '../../../services/quadraService';

// HORARIOS é gerado dinamicamente após carregar a quadra — ver uso abaixo
const HORARIOS_30 = gerarHorarios(30);
const HORARIOS_60 = gerarHorarios(60);

// ── helpers ──────────────────────────────────────────────────────────────────

const cellKey = (dia: string, hora: string) => `${dia}|${hora}`;

const horariosToSet = (horarios: any[], slots: string[]): Set<string> => {
  const set = new Set<string>();
  for (const h of horarios) {
    const iStart = slots.indexOf(h.horaInicio);
    const iEnd = slots.indexOf(h.horaFim);
    for (let i = iStart; i < iEnd; i++) set.add(cellKey(h.diaSemana, slots[i]));
  }
  return set;
};

/** Map de cellKey → valorPorHora a partir dos preços salvos */
const precosToMap = (precos: any[], slots: string[]): Record<string, number> => {
  const map: Record<string, number> = {};
  for (const p of precos) {
    const iStart = slots.indexOf(p.horaInicio);
    const iEnd = slots.indexOf(p.horaFim);
    for (let i = iStart; i < iEnd; i++) map[cellKey(p.diaSemana, slots[i])] = p.valorPorHora;
  }
  return map;
};

const groupToRanges = (selected: Set<string>, slots: string[]) => {
  const byDay: Record<string, string[]> = {};
  Array.from(selected).forEach(key => {
    const [dia, hora] = key.split('|');
    if (!slots.includes(hora)) return; // ignora slots de outro array
    if (!byDay[dia]) byDay[dia] = [];
    byDay[dia].push(hora);
  });
  const ranges: { diaSemana: string; horaInicio: string; horaFim: string }[] = [];
  for (const dia of Object.keys(byDay)) {
    const slotsSorted = byDay[dia].sort((a, b) => slots.indexOf(a) - slots.indexOf(b));
    if (!slotsSorted.length) continue;
    let start = slotsSorted[0], prev = slotsSorted[0];
    for (let i = 1; i < slotsSorted.length; i++) {
      if (slots.indexOf(slotsSorted[i]) === slots.indexOf(prev) + 1) { prev = slotsSorted[i]; }
      else {
        const fim = slots[slots.indexOf(prev) + 1];
        if (fim) ranges.push({ diaSemana: dia, horaInicio: start, horaFim: fim });
        start = slotsSorted[i]; prev = slotsSorted[i];
      }
    }
    const fim = slots[slots.indexOf(prev) + 1];
    if (fim) ranges.push({ diaSemana: dia, horaInicio: start, horaFim: fim });
  }
  return ranges;
};

// ── hook de drag reutilizável (seleção retangular) ───────────────────────────

const useDragGrid = (slotsRef: React.MutableRefObject<string[]>, initial: Set<string> = new Set()) => {
  const [selected, setSelected] = useState<Set<string>>(initial);
  const baseSelected = useRef<Set<string>>(initial); // estado antes do drag começar
  const dragging = useRef(false);
  const dragMode = useRef<'add' | 'remove'>('add');
  const dragStart = useRef<{ diaIdx: number; horaIdx: number } | null>(null);

  const applyRect = useCallback((endDiaIdx: number, endHoraIdx: number) => {
    if (!dragStart.current) return;
    const { diaIdx: sd, horaIdx: sh } = dragStart.current;
    const minD = Math.min(sd, endDiaIdx), maxD = Math.max(sd, endDiaIdx);
    const minH = Math.min(sh, endHoraIdx), maxH = Math.max(sh, endHoraIdx);
    setSelected(() => {
      const next = new Set(baseSelected.current);
      for (let d = minD; d <= maxD; d++) {
        for (let h = minH; h <= maxH; h++) {
          const key = cellKey(DIAS_SEMANA[d].value, slotsRef.current[h]);
          dragMode.current === 'add' ? next.add(key) : next.delete(key);
        }
      }
      return next;
    });
  }, []);

  const onCellDown = useCallback((key: string, diaIdx: number, horaIdx: number) => {
    dragging.current = true;
    dragMode.current = baseSelected.current.has(key) ? 'remove' : 'add';
    dragStart.current = { diaIdx, horaIdx };
    applyRect(diaIdx, horaIdx);
  }, [applyRect]);

  const onCellEnter = useCallback((diaIdx: number, horaIdx: number) => {
    if (!dragging.current) return;
    applyRect(diaIdx, horaIdx);
  }, [applyRect]);

  useEffect(() => {
    const up = () => {
      if (dragging.current) {
        dragging.current = false;
        dragStart.current = null;
        // captura o estado atual como nova base
        setSelected(prev => { baseSelected.current = new Set(prev); return prev; });
      }
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const setSelectedExternal = useCallback((val: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    setSelected(prev => {
      const next = typeof val === 'function' ? val(prev) : val;
      baseSelected.current = new Set(next);
      return next;
    });
  }, []);

  return { selected, setSelected: setSelectedExternal, onCellDown, onCellEnter };
};

// ── componente ────────────────────────────────────────────────────────────────

const AdminConfigurarQuadra: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [aba, setAba] = useState<'horarios' | 'precos'>('horarios');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [slotOriginal, setSlotOriginal] = useState<number | null>(null);
  const [slotPendente, setSlotPendente] = useState<number | null>(null);
  const [confirmandoSlot, setConfirmandoSlot] = useState(false);

  // Ref compartilhada de slots — atualizada sempre que HORARIOS muda
  const horariosRef = useRef<string[]>(HORARIOS_30);

  // Horários
  const [savedH, setSavedH] = useState<Set<string>>(new Set());
  const { selected: pendingH, setSelected: setPendingH, onCellDown: onCellDownH, onCellEnter: onCellEnterH } = useDragGrid(horariosRef);

  // Replicar
  const [modalReplicar, setModalReplicar] = useState(false);
  const [quadraOrigemId, setQuadraOrigemId] = useState('');
  const [replicando, setReplicando] = useState(false);
  const [replicarTambemPrecos, setReplicarTambemPrecos] = useState(true);

  // Preços
  const [precosMap, setPrecosMap] = useState<Record<string, number>>({});
  const [savedP, setSavedP] = useState<Set<string>>(new Set());
  const { selected: pendingP, setSelected: setPendingP, onCellDown: onCellDownP, onCellEnter: onCellEnterP } = useDragGrid(horariosRef);
  const [valorInput, setValorInput] = useState('');

  const { data: quadraData } = useQuery(QUADRA_ADMIN, { variables: { id } });
  const { data: horariosData, loading: loadingH, refetch: refetchH } = useQuery(HORARIOS_PERMITIDOS, { variables: { quadraId: id } });
  const { data: precosData, loading: loadingP, refetch: refetchP } = useQuery(PRECOS_QUADRA, { variables: { quadraId: id } });

  const [criarHorario] = useMutation(CRIAR_HORARIO_PERMITIDO);
  const [deletarHorario] = useMutation(DELETAR_HORARIO_PERMITIDO);
  const [deletarTodosHorarios] = useMutation(DELETAR_TODOS_HORARIOS);
  const [criarPreco] = useMutation(CRIAR_PRECO_QUADRA);
  const [deletarTodosPrecos] = useMutation(DELETAR_TODOS_PRECOS);
  const [atualizarQuadra] = useMutation(ATUALIZAR_QUADRA);

  const horarios: any[] = horariosData?.horariosPermitidos ?? [];
  const precos: any[] = precosData?.precosQuadra ?? [];
  const quadra = quadraData?.quadraPorId;
  const localId = quadra?.local?.id;
  const HORARIOS = (slotPendente ?? quadra?.duracaoSlotMinutos ?? 30) === 60 ? HORARIOS_60 : HORARIOS_30;
  horariosRef.current = HORARIOS;

  useEffect(() => {
    if (quadra && slotOriginal === null) {
      setSlotOriginal(quadra.duracaoSlotMinutos ?? 30);
    }
  }, [quadra]); // eslint-disable-line

  const { data: quadrasLocalData } = useQuery(QUADRAS_POR_LOCAL, { variables: { localId }, skip: !localId });
  const quadrasDoLocal: any[] = (quadrasLocalData?.quadrasPorLocal ?? []).filter((q: any) => q.id !== id);

  const [buscarHorariosOrigem] = useLazyQuery(HORARIOS_PERMITIDOS);
  const [buscarPrecosOrigem] = useLazyQuery(PRECOS_QUADRA);

  const handleReplicar = async () => {
    if (!quadraOrigemId) return;
    setReplicando(true); setErro('');
    try {
      const { data: hData } = await buscarHorariosOrigem({ variables: { quadraId: quadraOrigemId } });
      const horariosOrigem: any[] = hData?.horariosPermitidos ?? [];
      await deletarTodosHorarios({ variables: { quadraId: id } });
      for (const h of horariosOrigem) await criarHorario({ variables: { quadraId: id, input: { diaSemana: h.diaSemana, horaInicio: h.horaInicio, horaFim: h.horaFim } } });
      await refetchH();
      if (replicarTambemPrecos) {
        const { data: pData } = await buscarPrecosOrigem({ variables: { quadraId: quadraOrigemId } });
        const precosOrigem: any[] = pData?.precosQuadra ?? [];
        await deletarTodosPrecos({ variables: { quadraId: id } });
        for (const p of precosOrigem) await criarPreco({ variables: { quadraId: id, input: { diaSemana: p.diaSemana, horaInicio: p.horaInicio, horaFim: p.horaFim, valorPorHora: p.valorPorHora } } });
        await refetchP();
      }
      setModalReplicar(false);
      setQuadraOrigemId('');
    } catch (err: any) { setErro(err.message ?? 'Erro ao replicar.'); }
    finally { setReplicando(false); }
  };

  useEffect(() => {
    if (horarios.length > 0) {
      const set = horariosToSet(horarios, HORARIOS);
      setSavedH(set);
      setPendingH(new Set(Array.from(set)));
    }
  }, [horariosData, HORARIOS]); // eslint-disable-line

  useEffect(() => {
    if (precos.length > 0) {
      const map = precosToMap(precos, HORARIOS);
      setPrecosMap(map);
      setSavedP(new Set(Object.keys(map)));
      setPendingP(new Set());
    } else if (!salvando) {
      setPrecosMap({});
      setSavedP(new Set());
      setPendingP(new Set());
    }
  }, [precosData, HORARIOS]); // eslint-disable-line

  const handleAlterarSlot = async (novoSlot: number) => {
    if (!quadra) return;
    setSalvando(true); setErro('');
    try {
      await deletarTodosHorarios({ variables: { quadraId: id } });
      await atualizarQuadra({ variables: { id, input: {
        nome: quadra.nome,
        descricao: quadra.descricao ?? '',
        tipoPiso: quadra.tipoPiso,
        observacao: quadra.observacao ?? '',
        ativa: quadra.ativa,
        localId: quadra.local?.id,
        duracaoSlotMinutos: novoSlot,
      }}});
      setSlotOriginal(novoSlot);
      setSlotPendente(null);
      setConfirmandoSlot(false);
      setSavedH(new Set()); setPendingH(new Set());
      setSavedP(new Set()); setPendingP(new Set()); setPrecosMap({});
      await refetchH(); await refetchP();
      window.location.reload();
    } catch (err: any) { setErro(err.message ?? 'Erro ao alterar duração.'); }
    finally { setSalvando(false); }
  };

  // ── limpar horários (+ preços) ───────────────────────────────────────────

  const handleLimparHorarios = async () => {
    if (!window.confirm('Apagar todos os horários permitidos e a tabela de preços desta quadra?')) return;
    setSalvando(true); setErro('');
    try {
      await deletarTodosHorarios({ variables: { quadraId: id } });
      setSavedH(new Set()); setPendingH(new Set());
      setSavedP(new Set()); setPendingP(new Set()); setPrecosMap({});
      await refetchH(); await refetchP();
    } catch (err: any) { setErro(err.message ?? 'Erro ao limpar horários.'); }
    finally { setSalvando(false); }
  };

  // ── limpar preços ──────────────────────────────────────────────────────────

  const handleLimparPrecos = async () => {
    if (!window.confirm('Apagar toda a tabela de preços desta quadra?')) return;
    setSalvando(true); setErro('');
    try {
      await deletarTodosPrecos({ variables: { quadraId: id } });
      setSavedP(new Set()); setPendingP(new Set()); setPrecosMap({});
      await refetchP();
    } catch (err: any) { setErro(err.message ?? 'Erro ao limpar preços.'); }
    finally { setSalvando(false); }
  };

  // ── salvar horários ────────────────────────────────────────────────────────

  const handleSalvarHorarios = async () => {
    setErro(''); setSalvando(true);
    try {
      await deletarTodosHorarios({ variables: { quadraId: id } });
      for (const range of groupToRanges(pendingH, HORARIOS)) await criarHorario({ variables: { quadraId: id, input: range } });
      await refetchH();
    } catch (err: any) { setErro(err.message ?? 'Erro ao salvar horários.'); }
    finally { setSalvando(false); }
  };

  // ── salvar preços ──────────────────────────────────────────────────────────

  const handleSalvarPrecos = async () => {
    if (!valorInput || isNaN(parseFloat(valorInput))) { setErro('Informe um valor válido.'); return; }
    if (pendingP.size === 0) { setErro('Selecione ao menos um horário.'); return; }
    setErro(''); setSalvando(true);
    try {
      const valor = parseFloat(valorInput);
      // Monta mapa final: existentes + novos slots com novo valor
      const mapaFinal: Record<string, number> = { ...precosMap };
      pendingP.forEach(k => { mapaFinal[k] = valor; });
      // Deleta tudo e recria com o mapa final
      await deletarTodosPrecos({ variables: { quadraId: id } });
      // Agrupa por (dia, valor) para criar ranges contíguos com mesmo valor
      const grupos: Record<string, { dia: string; valor: number; slots: string[] }> = {};
      Object.entries(mapaFinal).forEach(([k, v]) => {
        const [dia, hora] = k.split('|');
        const gk = `${dia}||${v}`;
        if (!grupos[gk]) grupos[gk] = { dia, valor: v, slots: [] };
        grupos[gk].slots.push(hora);
      });
      for (const { dia, valor: v, slots } of Object.values(grupos)) {
        const fakeSet = new Set(slots.map(h => cellKey(dia, h)));
        for (const range of groupToRanges(fakeSet, HORARIOS)) {
          await criarPreco({ variables: { quadraId: id, input: { ...range, valorPorHora: v } } });
        }
      }
      setValorInput('');
      setPendingP(new Set());
      await refetchP();
    } catch (err: any) { setErro(err.message ?? 'Erro ao salvar preços.'); }
    finally { setSalvando(false); }
  };

  // ── render grid ────────────────────────────────────────────────────────────

  const renderGrid = (
    loading: boolean,
    saved: Set<string>,
    pending: Set<string>,
    onCellDown: (k: string, diaIdx: number, horaIdx: number) => void,
    onCellEnter: (diaIdx: number, horaIdx: number) => void,
    cellLabel?: (key: string) => string | null,
    allowedSlots?: Set<string>,
  ) => {
    if (loading) return <Load />;
    return (
      <div style={{ overflowX: 'auto', userSelect: 'none' }}>
        <table style={{ borderCollapse: 'collapse', minWidth: 560, width: '100%', tableLayout: 'fixed' }}>
          <thead>
            <tr>
              <th style={{ width: 52, padding: '4px 6px', fontSize: 11, color: '#aaa', fontWeight: 600 }}></th>
              {DIAS_SEMANA.map(d => (
                <th key={d.value} style={{ padding: '4px 2px', fontSize: 11, fontWeight: 700, textAlign: 'center', color: '#555' }}>{d.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {HORARIOS.map((hora, rowIdx) => (
              <tr key={hora}>
                <td style={{ fontSize: 10, color: '#aaa', paddingRight: 6, textAlign: 'right', whiteSpace: 'nowrap' }}>{hora}</td>
                {DIAS_SEMANA.map((d, colIdx) => {
                  const key = cellKey(d.value, hora);
                  const allowed = !allowedSlots || allowedSlots.has(key);
                  const isSaved = saved.has(key);
                  const isPending = pending.has(key);
                  let bg = rowIdx % 2 === 0 ? '#f5f5f5' : '#eeeeee';
                  let border = '1px solid #e0e0e0';
                  if (!allowedSlots) {
                    // grid de horários
                    if (isSaved && isPending)       { bg = '#c8e6c9'; border = '1px solid #a5d6a7'; }
                    else if (!isSaved && isPending) { bg = '#bbdefb'; border = '1px solid #90caf9'; }
                    else if (isSaved && !isPending) { bg = '#ffcdd2'; border = '1px solid #ef9a9a'; }
                  } else {
                    // grid de preços
                    if (!allowed) { /* cinza zebra */ }
                    else if (isPending)  { bg = '#fff9c4'; border = '1px solid #f9a825'; }
                    else if (isSaved)    { bg = '#c8e6c9'; border = '1px solid #a5d6a7'; }
                    else                { bg = '#bbdefb'; border = '1px solid #90caf9'; }
                  }
                  const label = allowed ? cellLabel?.(key) : null;
                  return (
                    <td
                      key={d.value}
                      onMouseDown={e => { if (!allowed) return; e.preventDefault(); onCellDown(key, colIdx, rowIdx); }}
                      onMouseEnter={() => allowed && onCellEnter(colIdx, rowIdx)}
                      style={{ background: bg, border, height: 18, cursor: allowed ? 'pointer' : 'default', transition: 'background 0.1s', textAlign: 'center', fontSize: 9, color: '#2e7d32', fontWeight: 700, overflow: 'hidden' }}
                    >
                      {label}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            <div className="d-flex align-items-center gap-2 mb-3">
              <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate(-1)}>
                <i className="feather-arrow-left me-1"></i> Voltar
              </button>
              <div>
                <h2 className="fw-700 font-md text-grey-900 mb-0">Configurar Quadra</h2>
                {quadra && <span className="font-xssss text-grey-500">{quadra.nome}</span>}
              </div>
            </div>

            <div className="card shadow-xss rounded-xxl border-0 mb-3">
              <div className="d-flex border-bottom">
                {(['horarios', 'precos'] as const).map(a => (
                  <button
                    key={a}
                    className={`btn flex-1 rounded-0 font-xsss fw-600 py-3 ${aba === a ? 'text-primary' : 'text-grey-500'}`}
                    style={{ borderBottom: aba === a ? '2px solid var(--bs-primary)' : '2px solid transparent' }}
                    onClick={() => setAba(a)}
                  >
                    {a === 'horarios' ? <><i className="feather-clock me-1"></i>Horários Permitidos</> : <><i className="feather-tag me-1"></i>Tabela de Preços</>}
                  </button>
                ))}
              </div>

              <div className="p-3">
                {erro && <div className="alert alert-danger font-xssss py-2 mb-3">{erro}</div>}

                {/* ── Horários ── */}
                {aba === 'horarios' && (
                  <>
                    <div className="d-flex align-items-center gap-3 mb-3 p-3 bg-greylight rounded-xl">
                      <span className="font-xssss fw-600 text-grey-600">Duração do horário:</span>
                      <select
                        className="form-select rounded-xl font-xsss"
                        style={{ width: 130 }}
                        value={slotPendente ?? slotOriginal ?? 30}
                        onChange={e => {
                          const novo = Number(e.target.value);
                          if (novo === slotOriginal) return;
                          const ok = window.confirm('Ao alterar a duração do horário, todos os horários permitidos e a tabela de preços desta quadra serão apagados. Deseja continuar?');
                          if (!ok) return;
                          handleAlterarSlot(novo);
                        }}
                      >
                        <option value={30}>30 minutos</option>
                        <option value={60}>1 hora</option>
                      </select>
                    </div>
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex gap-3 font-xssss text-grey-500">
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#c8e6c9', border: '1px solid #a5d6a7', borderRadius: 2, marginRight: 4 }}></span>Salvo</span>
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#bbdefb', border: '1px solid #90caf9', borderRadius: 2, marginRight: 4 }}></span>Novo</span>
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#ffcdd2', border: '1px solid #ef9a9a', borderRadius: 2, marginRight: 4 }}></span>Removido</span>
                      </div>
                      <div className="d-flex gap-2">
                        <button className="btn btn-outline-danger rounded-xl font-xssss fw-600 px-3 py-2" onClick={handleLimparHorarios} disabled={salvando || savedH.size === 0}>
                          <i className="feather-trash-2 me-1"></i>Limpar tudo
                        </button>
                        <button className="btn btn-light rounded-xl font-xssss fw-600 px-3 py-2" onClick={() => { setQuadraOrigemId(''); setModalReplicar(true); }} disabled={quadrasDoLocal.length === 0} title={quadrasDoLocal.length === 0 ? 'Nenhuma outra quadra neste local' : ''}>
                          <i className="feather-copy me-1"></i>Replicar de outra quadra
                        </button>
                        <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3 py-2" onClick={handleSalvarHorarios} disabled={salvando}>
                          {salvando ? 'Salvando...' : <><i className="feather-save me-1"></i>Salvar horários</>}
                        </button>
                      </div>
                    </div>
                    {renderGrid(loadingH, savedH, pendingH, onCellDownH, onCellEnterH)}
                  </>
                )}

                {/* ── Preços ── */}
                {aba === 'precos' && (
                  <>
                    <div className="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2">
                      <div className="d-flex gap-3 font-xssss text-grey-500">
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#c8e6c9', border: '1px solid #a5d6a7', borderRadius: 2, marginRight: 4 }}></span>Com valor</span>
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#bbdefb', border: '1px solid #90caf9', borderRadius: 2, marginRight: 4 }}></span>Sem valor</span>
                        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#fff9c4', border: '1px solid #f9a825', borderRadius: 2, marginRight: 4 }}></span>Selecionado</span>
                      </div>
                      <div className="d-flex align-items-center gap-2">
                        <span className="font-xssss text-grey-600 fw-600">Valor (R$):</span>
                        <input
                          type="number" step="0.01" min="0"
                          className="form-control rounded-xl font-xsss"
                          style={{ width: 100 }}
                          placeholder="0,00"
                          value={valorInput}
                          onChange={e => setValorInput(e.target.value)}
                        />
                        <button className="btn btn-outline-danger rounded-xl font-xssss fw-600 px-3 py-2" onClick={handleLimparPrecos} disabled={salvando || savedP.size === 0}>
                          <i className="feather-trash-2 me-1"></i>Limpar preços
                        </button>
                        <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-3 py-2" onClick={handleSalvarPrecos} disabled={salvando}>
                          {salvando ? 'Salvando...' : <><i className="feather-save me-1"></i>Salvar preços</>}
                        </button>
                      </div>
                    </div>
                    <p className="font-xssss text-grey-500 mb-2">Selecione os horários e informe o valor por hora para aplicar a todos.</p>
                    {renderGrid(loadingP, savedP, pendingP, onCellDownP, onCellEnterP,
                      key => {
                        const v = precosMap[key];
                        return v != null ? `R$${v}` : null;
                      },
                      savedH,
                    )}
                  </>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>
      <Appfooter />

      {/* Modal replicar */}
      {modalReplicar && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content rounded-xxl border-0">
              <div className="modal-header border-0 pb-0">
                <h5 className="fw-700 font-xss mb-0">Replicar configuração</h5>
                <button className="btn-close" onClick={() => setModalReplicar(false)}></button>
              </div>
              <div className="modal-body">
                <p className="font-xssss text-grey-500 mb-3">Selecione a quadra de origem. Os horários (e preços, se marcado) desta quadra serão substituídos pelos da quadra selecionada.</p>
                <div className="mb-3">
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Quadra de origem</label>
                  <select className="form-select rounded-xl font-xsss" value={quadraOrigemId} onChange={e => setQuadraOrigemId(e.target.value)}>
                    <option value="">Selecione...</option>
                    {quadrasDoLocal.map((q: any) => <option key={q.id} value={q.id}>{q.nome}</option>)}
                  </select>
                </div>
                <div className="form-check mb-3">
                  <input type="checkbox" className="form-check-input" id="replicarPrecos" checked={replicarTambemPrecos} onChange={e => setReplicarTambemPrecos(e.target.checked)} />
                  <label className="form-check-label font-xssss fw-600" htmlFor="replicarPrecos">Replicar também a tabela de preços</label>
                </div>
                {erro && <div className="alert alert-danger font-xssss py-2">{erro}</div>}
              </div>
              <div className="modal-footer border-0 pt-0">
                <button className="btn btn-light rounded-xl font-xssss fw-600 px-4" onClick={() => setModalReplicar(false)}>Cancelar</button>
                <button className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-4" onClick={handleReplicar} disabled={!quadraOrigemId || replicando}>
                  {replicando ? 'Replicando...' : 'Replicar'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default AdminConfigurarQuadra;
